import React, { useEffect, useState } from 'react';
import { ErrorMessage, Formik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import healthcareUpdate from '@custom/rtk-apis/patients/healthcareUpdate';
import { Loader, SuccessModal } from '@ohif/ui';
import { reset } from '@custom/store/slices/Patients/healthcareUpdateSlice';
import { customStyle } from '@custom/Utils/utils';
import { sampleFormSchema } from '@custom/Utils/schema/schema';
import dayjs from "dayjs";
import { Space, TimePicker } from "antd";
import { useLocation } from 'react-router-dom';
import { _roleType } from '@custom/Utils/constants/appConstants';

const HealthRecord = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const patientdata = useSelector((state: RootState) => state?.patientdetailResponce?.result);
  const healthcareUpdateData = useSelector((state: RootState) => state?.healthcareUpdateResponse);

  const [isSubmitting, setIsSubmitting] = useState(true);
  const [successModal, setSuccessModal] = useState(false);
  const [title, setTitle] = useState("");
  const role = sessionStorage.getItem('role');

  useEffect(() => {
    if (patientdata) {
      setIsSubmitting(false);  // Set to false when patient data is fetched
    }
  }, [patientdata]);

  function convertTo12HourFormat(time) {
    const [hour, minute] = time?.split(':');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const adjustedHour = (hour % 12 || 12).toString().padStart(2, '0'); // Pad single-digit hour with '0'
    return `${adjustedHour}:${minute} ${ampm}`;
  }

  function convertTo24HourFormat(time) {
    const [timePart, modifier] = time?.split(' ');
    let [hour, minute] = timePart.split(':').map(Number);

    if (modifier === 'PM' && hour !== 12) {
      hour += 12;
    } else if (modifier === 'AM' && hour === 12) {
      hour = 0;
    }

    return `${hour?.toString().padStart(2, '0')}:${minute?.toString().padStart(2, '0')}:00`;
  }

  const capitaliseLetter = str => {
    if (!str) {
      return null;
    }
    return str
      .split(/[ _]/)
      .map(word => {
        if (word) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        } else {
          return '';
        }
      })
      .join(' ');
  };

  const handleSubmit = (values) => {
    setIsSubmitting(true);  // Set submitting state
    let obj;
    const finalSampleLocation = values?.sampleLocation === 'Other' ? values?.sampleLocationOther || '' : values?.sampleLocation;
    {
      role === _roleType.technician || role === _roleType.pathologist || role === _roleType.admin || (role === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.technician) || (role === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.pathologist) ? (
        obj = {
          patientId: patientdata?.patientDetails?._id,
          healthCareInfo: {
            typeOfSample: values?.typeOfSample,
            sampleLocation: finalSampleLocation,
            sampleTime: convertTo12HourFormat(values?.sampleTime),
            slideCreationTime: convertTo12HourFormat(values?.slideCreationTime),
          },
        }
      ) : (
        obj = {
          patientId: patientdata?.patientDetails?._id,
          healthCareInfo: {
            allergies: values?.allergies,
            currentMedication: values?.currentMedications,
            relMedicalHist: values?.relevantMedicalHistory,
            prevSurgery: values?.previousSurgeriesOrProcedures,
          },
        }
      )
    }
    dispatch(healthcareUpdate(obj));  // Dispatching the API update action
  };

  useEffect(() => {
    if (healthcareUpdateData?.status === 'SUCCESS') {
      setIsSubmitting(false);  // Set submitting state to false when update is successful
      setTitle(healthcareUpdateData?.msg);
      setSuccessModal(true);
      dispatch(reset());
    } else if (healthcareUpdateData?.status === 'FAILED') {
      setIsSubmitting(false);  // Set submitting state to false when update fails
      dispatch(reset());
    }
  }, [healthcareUpdateData]);

  const sampleOptions = ['Breast', 'Lung', 'Colon', 'Colorectal', 'Prostate', 'Other'];
  const typeOfSampleRaw = patientdata?.patientDetails?.healthCareInfo?.typeOfSample
    ? patientdata?.patientDetails?.healthCareInfo?.typeOfSample
    : patientdata?.patientDetails?.healthCareInfo?.[0]?.typeOfSample || '';
  const rawSampleLoc = patientdata?.patientDetails?.healthCareInfo?.sampleLocation
    ? capitaliseLetter(patientdata?.patientDetails?.healthCareInfo?.sampleLocation)
    : patientdata?.patientDetails?.healthCareInfo?.[0]?.sampleLocation
      ? capitaliseLetter(patientdata?.patientDetails?.healthCareInfo?.[0]?.sampleLocation)
      : '';
  const isTissue = typeOfSampleRaw === 'Tissue';
  const initialSampleLocation = isTissue ? (rawSampleLoc && sampleOptions.includes(rawSampleLoc) ? rawSampleLoc : (rawSampleLoc ? 'Other' : '')) : rawSampleLoc;
  const initialSampleLocationOther = isTissue && rawSampleLoc && !sampleOptions.includes(rawSampleLoc) ? rawSampleLoc : '';

  return (
    <>
      <Formik
        validationSchema={
          (role === _roleType.technician ||
           role === _roleType.pathologist ||
           role === _roleType.admin ||
           (role === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.technician) ||
           (role === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.pathologist))
           ? sampleFormSchema
           : undefined
        }
        enableReinitialize={true}  // Ensure form reinitializes when initial values change
        initialValues={{
          typeOfSample: typeOfSampleRaw || '',
          sampleLocation: initialSampleLocation || '',
          sampleLocationOther: initialSampleLocationOther || '',
          sampleTime: patientdata?.patientDetails?.healthCareInfo?.sampleTime ? convertTo24HourFormat(patientdata?.patientDetails?.healthCareInfo?.sampleTime) : patientdata?.patientDetails?.healthCareInfo?.[0]?.sampleTime ? convertTo24HourFormat(patientdata?.patientDetails?.healthCareInfo?.[0]?.sampleTime) : '',
          slideCreationTime: patientdata?.patientDetails?.healthCareInfo?.slideCreationTime ? convertTo24HourFormat(patientdata?.patientDetails?.healthCareInfo?.slideCreationTime) : patientdata?.patientDetails?.healthCareInfo?.[0]?.slideCreationTime ? convertTo24HourFormat(patientdata?.patientDetails?.healthCareInfo?.[0]?.slideCreationTime) : '',
          allergies: patientdata?.patientDetails?.healthCareInfo?.[0]?.allergies || '',
          currentMedications: patientdata?.patientDetails?.healthCareInfo?.[0]?.currentMedication || '',
          relevantMedicalHistory: patientdata?.patientDetails?.healthCareInfo?.[0]?.relMedicalHist || '',
          previousSurgeriesOrProcedures: patientdata?.patientDetails?.healthCareInfo?.[0]?.prevSurgery || '',
        }}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({ values, handleChange, handleSubmit, handleBlur, setFieldValue }) => (
          <form onSubmit={handleSubmit} className='form--innersec'>
            <div className="mb-8 form__section">
              {patientdata?.appointmentDetials?.quesInfo?.length > 0 &&
                <div>
                  <table>
                    {patientdata?.appointmentDetials?.quesInfo?.map((item, index) => (
                      <tr key={index} className="question-div">
                        <td>
                          <strong>{item.question}</strong>
                        </td>
                        <td>
                          <p>{item.answer === "false" ? 'No' : 'Yes'}</p>
                        </td>
                      </tr>
                    ))}
                  </table>
                </div>
              }
              <div className="mt-8">
                {role === _roleType.technician || role === _roleType.pathologist || role === _roleType.admin || (role === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.technician) || (role === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.pathologist) ? (
                  <>
                    <div className='formRow formRow__4'>
                      <div className='formCol'>
                        <div className="form__Field">
                          <label className="form__label" htmlFor='typeOfSample'>
                            Type of sample<span className="required-asterisk">*</span>
                          </label>
                          <select
                            className="form__select form__select--sm"
                            id="typeOfSample"
                            name="typeOfSample"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.typeOfSample}
                            autoComplete='off'
                            style={customStyle.select}
                          >
                            <option value="" disabled>Select</option>
                            <option value="Tissue">Tissue</option>
                            <option value="Liquid">Liquid</option>
                          </select>
                        </div>
                        <ErrorMessage name="typeOfSample" component="div" className="error text-error" />
                      </div>
                      <div className='formCol'>
                        <div className="form__Field">
                          <label className="form__label" htmlFor='sampleLocation' >
                            Sample Location<span className="required-asterisk">*</span>
                          </label>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            {values.typeOfSample === 'Tissue' ? (
                              <select
                                className="form__select form__select--sm"
                                id="sampleLocation"
                                name="sampleLocation"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.sampleLocation}
                                autoComplete='off'
                                style={{ ...customStyle.select, flex: 1 }}
                              >
                                <option value="" disabled>Select</option>
                                <option value="Breast">Breast</option>
                                <option value="Lung">Lung</option>
                                <option value="Colon">Colon</option>
                                <option value="Colorectal">Colorectal</option>
                                <option value="Prostate">Prostate</option>
                                <option value="Other">Other</option>
                              </select>
                            ) : (
                              <input
                                className="form__input form__input--sm"
                                type="text"
                                id="sampleLocation"
                                name="sampleLocation"
                                placeholder="Sample Location"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.sampleLocation}
                                autoComplete='off'
                                style={{ flex: 1 }}
                              />
                            )}

                            {values.typeOfSample === 'Tissue' && values.sampleLocation === 'Other' && (
                              <input
                                className="form__input form__input--sm"
                                type="text"
                                id="sampleLocationOther"
                                name="sampleLocationOther"
                                placeholder="Please specify"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.sampleLocationOther || ''}
                                autoComplete='off'
                                style={{ width: 220 }}
                              />
                            )}
                          </div>
                        </div>
                        <ErrorMessage name="sampleLocation" component="div" className="error text-error" />
                      </div>
                      <div className='formCol'>
                        <div className="form__Field">
                          <label className="form__label" htmlFor='sampleTime'>
                            Sample Time<span className="required-asterisk">*</span>
                          </label>
                          <TimePicker
                            className='form__input form__input--sm'
                            use12Hours
                            format="hh:mm a"
                            value={
                              values.sampleTime
                                ? dayjs(values.sampleTime, "HH:mm:ss")
                                : null
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                const formattedTime = newValue?.format("HH:mm:ss");
                                setFieldValue("sampleTime", formattedTime)
                              }
                            }}
                            allowClear={false}
                          />
                        </div>
                        <ErrorMessage name="sampleTime" component="div" className="error text-error" />
                      </div>
                      <div className='formCol'>
                        <div className="form__Field">
                          <label className="form__label" htmlFor='slideCreationTime' >
                            Time of slide creation<span className="required-asterisk">*</span>
                          </label>
                          <TimePicker
                            className='form__input form__input--sm'
                            use12Hours
                            format="hh:mm a"
                            value={
                              values.slideCreationTime
                                ? dayjs(values.slideCreationTime, "HH:mm:ss")
                                : null
                            }
                            onChange={(newValue) => {
                              if (newValue) {
                                const formattedTime = newValue?.format("HH:mm:ss");
                                setFieldValue("slideCreationTime", formattedTime)
                              }
                            }}
                            allowClear={false}
                          />
                        </div>
                        <ErrorMessage name="slideCreationTime" component="div" className="error text-error" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="formRow">
                      <div className="formCol">
                        <div className="form-group">
                          <label htmlFor="allergies">Allergies</label>
                          <textarea
                            rows={3}
                            cols={30}
                            id="allergies"
                            name="allergies"
                            placeholder="Allergies"
                            onChange={handleChange}
                            value={values.allergies}
                            className='form__textarea form__textarea--sm'
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form-group">
                          <label htmlFor="currentMedications">Current Medications</label>
                          <textarea
                            rows={3}
                            cols={30}
                            id="currentMedications"
                            name="currentMedications"
                            placeholder="Medications"
                            onChange={handleChange}
                            value={values.currentMedications}
                            className='form__textarea form__textarea--sm'
                          />
                        </div>
                      </div>
                    </div>
                    <div className="formRow">
                      <div className="formCol">
                        <div className="form-group">
                          <label htmlFor="relevantMedicalHistory">Relevant Medical History</label>
                          <textarea
                            rows={3}
                            cols={30}
                            id="relevantMedicalHistory"
                            name="relevantMedicalHistory"
                            placeholder="Medical History"
                            onChange={handleChange}
                            value={values.relevantMedicalHistory}
                            className='form__textarea form__textarea--sm'
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form-group">
                          <label htmlFor="previousSurgeriesOrProcedures">Previous Surgeries or Procedures</label>
                          <textarea
                            rows={3}
                            cols={30}
                            id="previousSurgeriesOrProcedures"
                            name="previousSurgeriesOrProcedures"
                            placeholder="Surgeries"
                            onChange={handleChange}
                            value={values.previousSurgeriesOrProcedures}
                            className='form__textarea form__textarea--sm'
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button className="btn btn__primary mt-4" type="submit" disabled={isSubmitting}>Update</button>
            </div>
          </form>
        )}
      </Formik>
      <Loader
        isLoaderOpen={healthcareUpdateData?.loading || isSubmitting}
        title="Updating..."
      />
      <SuccessModal
        isSuccessModalOpen={successModal}
        title={title}
        handleCloseModal={() => setSuccessModal(false)}
      />
    </>
  );
};

export default HealthRecord;
